import React from 'react'
import { useSelector } from 'react-redux'
import { truncate, find, isEmpty } from 'lodash'
import { Link } from 'react-router-dom'
import {
  IconX,
  IconCheck,
  IconCircleDotted,
  IconExclamationCircle,
  IconInfoCircle,
  IconFile,
  IconCircleCheckFilled,
  IconAlertCircleFilled,
  IconArrowUpRight,
} from '@tabler/icons-react'
import classNames from 'classnames'
import { Button } from '@/components/buttons/Button'
import { Loader } from '@/components/loaders/Loader'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'

import './FilePreview.scss'

export const FilePreview = ({
  allowEdits = true,
  name,
  file,
  dark,
  formikProps,
  useIconLayout,
  rowVariant,
}) => {
  if (!file) {
    return null
  }
  const fileExtension = file.type?.split('/')[1]
  const { files } = useSelector((state) => state.audioIngestion)

  const findFile = () => {
    if (!isEmpty(files) && file) {
      return find(files, (item) => {
        return item.instance.name === file.name
      })
    }

    return null
  }
  const getFileStatus = (status) => {
    const foundFile = findFile()
    return foundFile ? foundFile[status] : false
  }

  const getCallIdForFile = () => {
    const foundFile = findFile()

    return foundFile ? foundFile.callId : null
  }

  const getErrorMsg = () => {
    const foundFile = findFile()

    return foundFile ? foundFile.errorMsg : null
  }

  const handleRemoveClick = (event) => {
    event.stopPropagation()

    const clonedFiles = formikProps.values[name].filter((clonedFile) => {
      return clonedFile.name !== file.name
    })
    formikProps.setSubmitting(false)
    formikProps.setFieldValue(name, clonedFiles)
  }

  if (rowVariant === 'card') {
    const isUploading = getFileStatus('isUploading')
    const isDone = getFileStatus('isDone')
    const isError = getFileStatus('isError')
    const errorMsg = isError ? getErrorMsg() : null
    const showRemove = allowEdits && !isUploading && !isDone && !isError

    return (
      <div className={classNames('file-row', { 'file-row--error': isError })}>
        <div className="file-row__main">
          <div className="file-row__leading">
            {isUploading && <Loader size="small" />}
            {isDone && !isError && (
              <IconCircleCheckFilled className="icon-svg file-row__status-icon file-row__status-icon--success" />
            )}
            {isError && (
              <IconAlertCircleFilled className="icon-svg file-row__status-icon file-row__status-icon--critical" />
            )}
            {!isUploading && !isDone && !isError && (
              <IconFile className="icon-svg file-row__status-icon file-row__status-icon--default" />
            )}
            <div className="file-row__name">{file.name}</div>
          </div>
          <div className="file-row__trailing">
            {isDone && !isError && getCallIdForFile() && (
              <Link
                to={`/calls/${getCallIdForFile()}`}
                target="_blank"
                className="file-row__view-link"
                aria-label={`View call for ${file.name}`}
                onClick={(event) => event.stopPropagation()}
              >
                <IconArrowUpRight className="icon-svg" />
              </Link>
            )}
            {showRemove && (
              <Button
                basic
                square
                xs
                icon={<IconX />}
                onClick={handleRemoveClick}
                aria-label={`Remove ${file.name}`}
              />
            )}
          </div>
        </div>
        {isError && errorMsg && <div className="file-row__error">{errorMsg}</div>}
      </div>
    )
  }

  return (
    <div className={classNames('file-preview', { dark })}>
      {useIconLayout && (
        <>
          <div
            className="file-clear"
            onClick={(err) => {
              err.stopPropagation()

              formikProps.setFieldValue(name, null)
              formikProps.setSubmitting(false)
            }}
          >
            <IconX className="icon-svg" />
          </div>
          <div className="file-type">.{fileExtension}</div>
          <div className="file-name">
            <h3>{truncate(file.name, 30)}</h3>
          </div>
        </>
      )}
      {!useIconLayout && (
        <div className="flex-space-between flex-align-center full-width">
          <div className="flex-align-center">
            {getFileStatus('isUploading') && <Loader size="small" />}
            {getFileStatus('isDone') && !getFileStatus('isError') && (
              <IconCheck className="icon-svg status-success" />
            )}
            {getFileStatus('isError') && (
              <IconExclamationCircle className="icon-svg status-critical" />
            )}
            {!getFileStatus('isUploading') && !getFileStatus('isDone') && !allowEdits && (
              <IconCircleDotted className="icon-svg status-warning" />
            )}
            <div className="ml">{file.name}</div>
            {getFileStatus('languageRetried') && (
              <InteractiveTooltip content="Language was auto-corrected">
                <IconInfoCircle className="icon-svg ml status-warning" />
              </InteractiveTooltip>
            )}
          </div>
          {getFileStatus('isDone') && !getFileStatus('isError') && (
            <Link to={`/calls/${getCallIdForFile()}`} target="_blank">
              View call
            </Link>
          )}
          {getFileStatus('isError') && <div className="status-critical">{getErrorMsg()}</div>}
          {allowEdits && (
            <div className="balto-button critical" onClick={handleRemoveClick}>
              Remove
            </div>
          )}
        </div>
      )}
    </div>
  )
}
